import React from 'react';
import { logger } from '../utils/logger';
import * as Sentry from '@sentry/react';
import { IonContent, IonPage, IonButton } from '@ionic/react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  gameName?: string;
  resetPath?: string;
  onReset?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logger.error('Error caught by boundary:', error, errorInfo);

    // Send to Sentry
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });

    // Call custom reset handler if provided
    if (this.props.onReset) {
      this.props.onReset();
    } else if (this.props.resetPath) {
      window.location.href = this.props.resetPath;
    } else {
      window.location.href = '/';
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <IonPage>
          <IonContent className="ion-padding">
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                textAlign: 'center',
                padding: '20px',
              }}
            >
              <div style={{ fontSize: '64px', marginBottom: '20px' }}>😕</div>
              <h1 style={{ fontSize: '24px', marginBottom: '10px' }}>
                {this.props.gameName
                  ? `${this.props.gameName} Encountered an Error`
                  : 'Oops! Something went wrong'}
              </h1>
              <p style={{ color: '#666', marginBottom: '30px', maxWidth: '400px' }}>
                {this.props.gameName
                  ? `Don't worry - your progress has been saved automatically. You can restart the game or go back to the game hub.`
                  : `We've been notified of the issue and are working to fix it. Please try refreshing the page or going back to the home screen.`}
              </p>

              {import.meta.env.DEV && this.state.error && (
                <div
                  style={{
                    background: '#fee2e2',
                    border: '1px solid #ef4444',
                    borderRadius: '8px',
                    padding: '15px',
                    marginBottom: '20px',
                    maxWidth: '600px',
                    textAlign: 'left',
                    overflow: 'auto',
                  }}
                >
                  <strong style={{ color: '#991b1b' }}>Error Details:</strong>
                  <pre style={{ fontSize: '12px', marginTop: '10px', whiteSpace: 'pre-wrap' }}>
                    {this.state.error.message}
                  </pre>
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
                <IonButton onClick={this.handleReset}>
                  {this.props.gameName ? 'Restart Game' : 'Go to Home'}
                </IonButton>
                {this.props.gameName && (
                  <IonButton fill="outline" onClick={() => window.history.back()}>
                    Back to Game Hub
                  </IonButton>
                )}
                <IonButton fill={this.props.gameName ? 'clear' : 'outline'} onClick={() => window.location.reload()}>
                  Refresh Page
                </IonButton>
              </div>
            </div>
          </IonContent>
        </IonPage>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
